import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Lock, Save, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import AdminGoalPicker from "@/components/AdminGoalPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCORES = [1, 2, 3, 4, 5];
const EV_STATUS = {
  pending: ["بانتظار مراجعة المدير", "bg-amber-100 text-amber-800 border-amber-300"],
  accepted: ["مقبول", "bg-emerald-100 text-emerald-800 border-emerald-300"],
  rejected: ["مرفوض", "bg-rose-100 text-rose-700 border-rose-300"],
};

export default function StaffAdminEval({ staffFetch, employee, token }) {
  const [period, setPeriod] = useState("الفصل الأول");
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [scores, setScores] = useState({});
  const [savingGoals, setSavingGoals] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const fileInputs = useRef({});

  const load = useCallback(async (p) => {
    try {
      const d = await staffFetch(`/staff/admin-eval?period=${encodeURIComponent(p)}`);
      setData(d);
      setScores(d.self_scores || {});
    } catch (e) {
      if (e.message !== "session-ended") toast.error(e.message);
    }
  }, [staffFetch]);

  useEffect(() => { setData(null); load(period); }, [period, load]);

  const saveGoals = async () => {
    setSavingGoals(true);
    try {
      await staffFetch("/staff/admin-eval/goals", {
        method: "PUT", body: JSON.stringify({ period, goal_ids: picked }),
      });
      toast.success("حُفظت أهدافك وقُفلت — لا يمكن تعديلها إلا بموافقة المدير");
      await load(period);
    } catch (e) {
      if (e.message !== "session-ended") toast.error(e.message);
    } finally { setSavingGoals(false); }
  };

  const saveSelf = async () => {
    setSaving(true);
    try {
      const d = await staffFetch("/staff/admin-eval/self", {
        method: "PUT", body: JSON.stringify({ period, scores }),
      });
      setData((prev) => ({ ...prev, self_scores: d.scores, self_total: d.total, self_pct: d.pct }));
      toast.success(`حُفظ تقييمك الذاتي — النتيجة ${d.pct}٪`);
    } catch (e) {
      if (e.message !== "session-ended") toast.error(e.message);
    } finally { setSaving(false); }
  };

  const upload = async (goalId, file) => {
    if (!file) return;
    setUploading(goalId);
    try {
      const fd = new FormData();
      fd.append("period", period);
      fd.append("goal_id", goalId);
      fd.append("file", file);
      const res = await fetch(`${API}/staff/admin-eval/evidence/upload`, {
        method: "POST", headers: { "X-Staff-Token": token }, body: fd,
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "فشل الرفع");
      toast.success("تم رفع الشاهد — بانتظار مراجعة المدير");
      await load(period);
    } catch (e) {
      toast.error(typeof e.message === "string" ? e.message : "فشل الرفع");
    } finally {
      setUploading("");
      if (fileInputs.current[goalId]) fileInputs.current[goalId].value = "";
    }
  };

  const removeEvidence = async (evId) => {
    try {
      await staffFetch(`/staff/admin-eval/evidence/${evId}`, { method: "DELETE" });
      toast.success("حُذف الشاهد");
      await load(period);
    } catch (e) {
      if (e.message !== "session-ended") toast.error(e.message);
    }
  };

  const viewFile = async (evId) => {
    try {
      const res = await fetch(`${API}/staff/evidence/file/${evId}`, { headers: { "X-Staff-Token": token } });
      if (!res.ok) throw new Error("تعذر فتح الملف");
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { toast.error(e.message); }
  };

  if (!data) return <p className="text-center text-sm text-slate-400 py-10">جارٍ التحميل...</p>;

  const periods = ["الفصل الأول", "الفصل الثاني", "السنة الكاملة"];

  return (
    <div className="space-y-3" data-testid="staff-admin-eval">
      <div className="bg-white rounded-2xl border p-3 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold text-slate-500">الفترة:</span>
        {periods.map((p) => (
          <button key={p} onClick={() => setPeriod(p)} data-testid={`staff-admin-period-${p}`}
            className={`rounded-full px-3 py-1.5 text-[11px] font-bold border transition-colors ${
              period === p ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-slate-600"}`}>
            {p}
          </button>
        ))}
        <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"
          data-testid="staff-admin-year">{data.year}</span>
      </div>

      <div className="bg-emerald-950 text-white rounded-2xl px-4 py-3">
        <p className="font-extrabold text-sm" data-testid="staff-admin-title">نموذج تقييم إداري — {employee.name}</p>
        <p className="text-[11px] text-emerald-300/80 mt-0.5">{period} — {data.year}</p>
      </div>

      <div className="bg-sky-50/60 border border-sky-200 rounded-2xl px-4 py-3 flex flex-wrap items-center gap-2"
        data-testid="staff-admin-criterion1">
        <Lock className="w-4 h-4 text-sky-700" />
        <p className="text-xs font-bold flex-1">أداء الواجبات الوظيفية (يُحسب آلياً من حضورك)</p>
        <span className="text-[11px] font-bold text-sky-800 bg-sky-100 border border-sky-300 rounded-full px-2.5 py-0.5"
          data-testid="staff-admin-c1-score">{data.criterion1.score}/5</span>
        <span className="text-[10px] text-slate-400 w-full">{data.criterion1.basis}</span>
      </div>

      {!data.selected ? (
        <div className="bg-white rounded-2xl border p-4 space-y-3" data-testid="staff-admin-goal-select">
          <p className="text-sm font-extrabold text-slate-700">اختر أهدافك لهذه الفترة</p>
          <p className="text-[11px] text-slate-400 leading-5">
            اختر من {data.min_goals} إلى {data.max_goals} أهداف من المحاور التالية. بعد الحفظ تُقفل الأهداف
            ولا يمكن تعديلها إلا بموافقة المدير. الوزن يُوزع بالتساوي بين الأهداف المختارة ومعيار الواجبات الوظيفية.
          </p>
          <AdminGoalPicker catalog={data} picked={picked} setPicked={setPicked}
            onSave={saveGoals} saving={savingGoals} testIdPrefix="staff-goal" />
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl border divide-y" data-testid="staff-admin-goals">
            {data.goals.map((g, gi) => (
              <div key={g.goal.id} className="px-4 py-3 space-y-2" data-testid={`staff-admin-goal-${g.goal.id}`}>
                <div className="flex items-start gap-2.5 flex-wrap">
                  <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold flex items-center justify-center shrink-0">{gi + 1}</span>
                  <div className="flex-1 min-w-[180px]">
                    <p className="text-xs font-bold leading-6">{g.goal.title}</p>
                    <p className="text-[11px] text-slate-400 leading-5">{g.goal.desc}</p>
                  </div>
                  <span className="text-[10px] font-bold text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                    data-testid={`staff-admin-goal-score-${g.goal.id}`}>درجة الهدف: {g.score}/5</span>
                </div>
                <div className="mr-8 flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold text-slate-400">تقييمي الذاتي:</span>
                  <div className="flex gap-1" data-testid={`staff-admin-self-${g.goal.id}`}>
                    {SCORES.map((s) => (
                      <button key={s} onClick={() => setScores({ ...scores, [g.goal.id]: s })}
                        data-testid={`staff-admin-self-${g.goal.id}-${s}`}
                        className={`w-7 h-7 rounded-lg border text-[11px] font-extrabold transition-colors ${
                          Number(scores[g.goal.id]) === s ? "bg-emerald-900 text-white border-emerald-900" : "bg-white hover:bg-emerald-50"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mr-8 space-y-1.5" data-testid={`staff-admin-evidence-${g.goal.id}`}>
                  {g.evidence.map((ev) => {
                    const st = EV_STATUS[ev.status];
                    return (
                      <div key={ev.id} className="flex items-center gap-2 flex-wrap rounded-lg border bg-slate-50/60 px-3 py-2"
                        data-testid={`staff-admin-evidence-item-${ev.id}`}>
                        <FileText className="w-3.5 h-3.5 text-slate-400" />
                        <p className="flex-1 min-w-[120px] text-[11px] font-semibold text-slate-700">{ev.original_name || "شاهد مرفوع"}</p>
                        <button onClick={() => viewFile(ev.id)} data-testid={`staff-admin-evidence-view-${ev.id}`}
                          className="text-[11px] font-bold text-sky-700 hover:underline">عرض</button>
                        <span className={`text-[10px] font-bold border rounded-full px-2 py-0.5 ${st[1]}`}
                          data-testid={`staff-admin-evidence-status-${ev.id}`}>{st[0]}</span>
                        {ev.status === "rejected" && ev.note && (
                          <span className="text-[10px] text-rose-600">ملاحظة المدير: {ev.note}</span>
                        )}
                        {ev.status === "pending" && (
                          <button onClick={() => removeEvidence(ev.id)}
                            data-testid={`staff-admin-evidence-del-${ev.id}`}
                            className="flex items-center gap-0.5 text-[11px] font-bold text-rose-600 hover:text-rose-800">
                            <Trash2 className="w-3.5 h-3.5" /> حذف
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <input type="file" accept="image/*,application/pdf" className="hidden"
                    ref={(el) => { fileInputs.current[g.goal.id] = el; }}
                    data-testid={`staff-admin-evidence-input-${g.goal.id}`}
                    onChange={(e) => upload(g.goal.id, e.target.files?.[0])} />
                  <Button size="sm" variant="outline" disabled={uploading === g.goal.id}
                    onClick={() => fileInputs.current[g.goal.id]?.click()}
                    data-testid={`staff-admin-evidence-upload-${g.goal.id}`}
                    className="h-7 text-[11px] gap-1 px-2.5">
                    {uploading === g.goal.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    رفع شاهد للهدف
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border p-3 flex items-center gap-3 flex-wrap">
            <span className="text-xs font-bold text-sky-900 bg-sky-50 border border-sky-200 rounded-full px-3 py-1"
              data-testid="staff-admin-self-total">
              ذاتي: {data.self_total != null ? `${data.self_total} / 5 (${data.self_pct}٪)` : "لم تحفظ بعد"}
            </span>
            <span className="text-xs font-bold text-emerald-900 bg-emerald-100 border border-emerald-200 rounded-full px-3 py-1"
              data-testid="staff-admin-manager-total">
              تقييم المدير: {data.total != null ? `${data.total} / 5 (${data.pct}٪)` : "بانتظار مراجعة الشواهد"}
            </span>
            <Button onClick={saveSelf} disabled={saving} data-testid="staff-admin-self-save"
              className="mr-auto h-10 text-xs font-bold gap-1.5 bg-emerald-900 hover:bg-emerald-950 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} حفظ التقييم الذاتي
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
